<template>
  <div>
    <div v-if="api_accounts.length">
      <div v-if="!is_edit_mode">
        <Message type="info">キャンペーンを開催するアカウントを設定してください。</Message>
      </div>
      <div class="account-info">
        <Panel>
          <FormItemLabel class="account-info-label" label="実施アカウント" />

          <div v-if="!is_edit_mode">
            <el-dropdown trigger="click" @command="changeAccount">
              <span class="account-dropdown-link">
                <Account
                  v-if="account"
                  :name="account.name"
                  :image="account.picture_url"
                  :symbol="account.target_sns"
                />
                <Icon name="menu-down" space="left" />
              </span>

              <el-dropdown-menu slot="dropdown" class="account-dropdown">
                <el-dropdown-item v-for="account of api_accounts" :key="account.key" :command="account.key">
                  <div class="account-dropdown-item">
                    <Account :name="account.name" :image="account.picture_url" :symbol="account.target_sns" />
                  </div>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <div v-if="is_twitter" class="account-info-default account-info-small-size">
              ・フォロー判定: ATELUで自動取得
              <br />
              ・DM配信: ATELUで一括配信
            </div>

            <div v-if="is_instagram" class="account-info-error account-info-small-size">
              ・フォロー判定: 公式Instagramでチェックしていただく必要があります
              <br />
              ・DM配信: 通常キャンペーンの場合、公式Instagramから直接配信していただく必要があります
            </div>

            <div v-if="is_tiktok" class="account-info-error account-info-small-size">
              ・フォロー判定: 公式TikTokでチェックしていただく必要があります
              <br />
              ・DM配信: 公式TikTokから直接配信していただく必要があります
            </div>

            <div v-if="is_token_expired" class="account-info-error">
              アクセストークンの有効期限が切れています。グループ設定画面から更新してください。
              <br />
              アクセストークンの更新方法は
              <a href="https://help-atelu.comnico.jp/update-access-token" target="_blank" class="account-info-link">
                こちら
              </a>
              をご確認ください。
            </div>
          </div>

          <div v-if="is_edit_mode">
            <div v-if="is_account_deleted" class="account-info">
              <Message type="danger"> キャンペーン実施アカウントが削除されました。 </Message>
            </div>
            <div v-if="selected_account">
              <Account
                :name="selected_account.name"
                :image="selected_account.picture_url"
                :symbol="selected_account.target_sns"
              />
            </div>
          </div>
        </Panel>
      </div>
    </div>

    <div v-else>
      <Message type="danger">
        キャンペーンを実施可能なアカウントがありません。
        <br />
        アカウントの登録が済んでいないか、アクセストークンの有効期限が切れています。
        <br />
        [グループ設定] を確認するか、管理権限を持つユーザーに確認を依頼してください。
      </Message>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import Message from '@/client/components/_atoms/Message.vue'
import Panel from '@/client/components/_atoms/Panel.vue'
import Account from '@/client/components/_molecules/Account.vue'
import FormItemLabel from '@/client/components/_molecules/FormItemLabel.vue'
import { TrackingService } from '@/client/services'
import { useCampaignDialogStore } from '@/client/store/useCampaignDialogStore'

export default defineComponent({
  name: 'CampaignCreateAccount',
  components: {
    Account,
    FormItemLabel,
    Icon,
    Message,
    Panel
  },
  methods: {
    ...mapActions(useCampaignDialogStore, ['setForm', 'getDefaultKeywords']),
    cancel(payload: unknown) {
      this.$emit('cancel', payload)
      return payload
    },
    /**
     * アカウントを変更
     */
    async changeAccount(account_key: string) {
      TrackingService.sendEvent('select:キャンペーンの新規作成|アカウント|実施アカウント')

      const account = this.api_accounts.find(account => account.key === account_key)

      if (!account) {
        return
      }

      const is_valid = ['start_date', 'start_time', 'end_date', 'end_time'].some(v => this.form[v])

      this.account_id = account.id
      this.target_sns = account.target_sns

      if (this.account.target_sns !== account.target_sns && is_valid) {
        const confirm = window.confirm('この画面以降の編集内容がリセットされます。よろしいですか？')

        if (!confirm) {
          return
        }

        await this.setForm({
          title: this.form.title,
          account_id: account.id,
          target_sns: this.target_sns,
          start_date: null,
          start_time: null,
          end_date: null,
          end_time: null,
          entry_action_type: 'retweet',
          campaign_type: 'keyword',
          keywords: [],
          or_keywords: [],
          is_include_mention: false,
          is_include_reply: false,
          is_keyword_judgment: true
        })

        await this.getDefaultKeywords()
      } else {
        await this.setForm({
          ...this.form,
          account_id: this.account_id,
          target_sns: this.target_sns
        })
      }
    }
  },
  data() {
    return {
      account_id: '' as string | number,
      target_sns: 'twitter'
    }
  },
  created() {
    if (this.api_accounts.length) {
      this.account_id = this.api_accounts[0].id
      this.target_sns = this.api_accounts[0].target_sns
    }

    if (this.form.account_id) {
      this.account_id = this.form.account_id
      this.target_sns = this.form.target_sns
    }

    if (this.account_id) {
      this.setForm({ ...this.form, account_id: this.account_id, target_sns: this.target_sns })
    }
  },
  watch: {
    api_accounts() {
      if (this.api_accounts.length) {
        this.account_id = this.api_accounts[0].id

        const target_sns = ['instagram', 'tiktok', 'twitter'].includes(this.api_accounts[0].target_sns)
          ? this.api_accounts[0].target_sns
          : 'twitter'

        this.setForm({ ...this.form, target_sns, account_id: this.account_id })
      }
    }
  },
  computed: {
    ...mapState(useCampaignDialogStore, ['api_accounts', 'api_campaign', 'form', 'mode']),
    account() {
      const account = this.api_accounts.find(account => {
        return !this.form.account_id
          ? account.id === this.account_id && account.target_sns === this.target_sns
          : account.id === this.form.account_id && account.target_sns === this.form.target_sns
      })

      return account || { name: '', picture_url: '', target_sns: 'twitter', is_token_expired: false, is_verified: true }
    },
    is_verified() {
      return this.account.is_verified
    },
    is_twitter() {
      return this.account.target_sns === 'twitter'
    },
    is_instagram() {
      return this.account.target_sns === 'instagram'
    },
    is_tiktok() {
      return this.account.target_sns === 'tiktok'
    },
    is_token_expired() {
      return this.account.is_token_expired
    },
    is_edit_mode() {
      return this.mode === 'edit'
    },
    selected_account() {
      return this.api_campaign && this.api_campaign.account
        ? {
            name: this.api_campaign.account.name,
            picture_url: this.api_campaign.account.picture_url,
            target_sns: this.api_campaign.target_sns
          }
        : null
    },
    is_account_deleted() {
      return !this.selected_account && this.mode === 'edit'
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.account-dropdown-link {
  display: flex;
  padding: $spacing-unit;
  cursor: pointer;
  border: 1px solid $border-color;
  border-radius: math.div($spacing-unit, 2);

  &:hover {
    border: 1px solid #bdbdbd;
  }

  &:focus {
    border: 1px solid $primary-color;
  }
}

.account-dropdown-item {
  padding: math.div($spacing-unit, 2) 0;
}

.account-info {
  margin-top: $spacing-unit * 2;
}

.account-dropdown {
  max-height: $spacing-unit * 38;
  overflow: auto;
}

.account-info-label {
  margin-bottom: $spacing-unit;
}

.account-info-small-size {
  font-size: $font-small-size;
}

.account-info-default {
  margin-top: $spacing-unit;
  color: $sub-text-color;
}

.account-info-error {
  margin-top: $spacing-unit;
  color: $danger-color;
}

.account-info-link {
  color: $link-color;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
